import { Button, Form, FormProps, Input, Select } from 'antd'
import { useEffect, useState } from 'react'
import { attemptGetBusinessesList } from '../../utils/redux/businessesSlice'
import { attemptGetDaytimesList } from '../../utils/redux/daytimesSlice'
import { attemptGetMoodsList, FolderOrderBy } from '../../utils/redux/moodsSlice'
import { EditPlaylistDTO, Playlist } from '../../utils/redux/playlistsSlice'
import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import { attemptGetStylesList } from '../../utils/redux/stylesSlice'
import { PlaylistPosterFormItem } from './PlaylistPosterFormItem'
import PlaylistSongsFormItem from './PlaylistSongsFormItem'
import { Song } from '../../utils/redux/songsSlice'
import TextWithTranslationFormItem from '../TextWithTranslationFormItem'

interface Props extends FormProps<EditPlaylistDTO> {
  isLoading: boolean
  userOrder?: string
  playlist?: Playlist
}

export default function PlaylistForm({
  isLoading,
  userOrder,
  playlist,
  ...formProps
}: Props) {
  // const {onFinish, initialValues} = formProps
  const { moods } = useAppSelector((state) => state.moods)
  const { businesses } = useAppSelector((state) => state.businesses)
  const { styles } = useAppSelector((state) => state.styles)
  const { daytimes } = useAppSelector((state) => state.daytimes)
  const dispatch = useAppDispatch()
  const [songs, setSongs] = useState<Song[]>([])
  const [playlistForm] = Form.useForm<EditPlaylistDTO>()

  useEffect(() => {
    if (playlist) {
      playlistForm.setFieldsValue({
        ...playlist,
        songs: playlist.songs.map((song) => song._id),
        moods: playlist.moods?.map((mood) => mood._id),
        businesses: playlist.businesses?.map((business) => business._id),
        daytimes: playlist.daytimes?.map((daytime) => daytime._id),
        styles: playlist.styles?.map((style) => style._id),
      })
      setSongs(playlist?.songs || [])
    }
  }, [playlist])

  useEffect(() => {
    playlistForm.setFieldsValue({
      songs: songs.map((song) => song._id),
    })
  }, [songs])

  useEffect(() => {
    dispatch(
      attemptGetMoodsList({
        skip: 0,
        limit: 200,
        oderBy: FolderOrderBy.TITLE,
      }),
    )

    dispatch(
      attemptGetDaytimesList({
        skip: 0,
        limit: 200,
        oderBy: FolderOrderBy.TITLE,
      }),
    )

    dispatch(
      attemptGetStylesList({
        skip: 0,
        limit: 200,
        oderBy: FolderOrderBy.TITLE,
      }),
    )

    dispatch(
      attemptGetBusinessesList({
        skip: 0,
        limit: 200,
        oderBy: FolderOrderBy.TITLE,
      }),
    )
  }, [])

  //TODO: move to createSelector
  const moodsOptions = Object.values(moods).map((mood) => {
    return { label: mood.title, value: mood._id }
  })

  const businessesOptions = Object.values(businesses).map((business) => {
    return { label: business.title, value: business._id }
  })

  const daytimesOptions = Object.values(daytimes).map((daytime) => {
    return { label: daytime.title, value: daytime._id }
  })

  const stylesOptions = Object.values(styles).map((style) => {
    return { label: style.title, value: style._id }
  })

  return (
    <>
      <Form 
        layout="vertical" 
        form={playlistForm}
        {...formProps}
      >
        <Form.Item name="image" rules={[{ required: true }]}>
          <PlaylistPosterFormItem uploadedDestination="playlists" />
        </Form.Item>
        <Form.Item
          name="title"
          rules={[{ required: true, message: 'Please input playlist title!' }]}
        >
          <Input placeholder="Title" />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
        >
          <TextWithTranslationFormItem fieldName={"description"} form={playlistForm}/>
        </Form.Item>
        <Form.Item
          name="songs"
          rules={[{ required: true, message: 'Playlist can not be empty!' }]}
        >
          <PlaylistSongsFormItem
            playlistSongs={songs}
            onAdd={(song) => {
              setSongs((prev) => [...prev, song])
            }}
            onDelete={(song) => {
              setSongs((prev) => prev.filter((s) => {
                return s._id !== song._id
              }))
            }}
            setPlaylistSongs={setSongs}
          />
        </Form.Item>
        {!userOrder && (
          <>
            <Form.Item name="styles">
              <Select
                placeholder="Style"
                options={stylesOptions}
                mode="multiple"
                allowClear
              />
            </Form.Item>
            <Form.Item name="moods">
              <Select
                placeholder="Mood"
                options={moodsOptions}
                mode="multiple"
                allowClear
              />
            </Form.Item>
            <Form.Item name="businesses">
              <Select
                placeholder="Business"
                options={businessesOptions}
                mode="multiple"
                allowClear
              />
            </Form.Item>
            <Form.Item name="daytimes">
              <Select
                placeholder="Daytime"
                options={daytimesOptions}
                mode="multiple"
                allowClear
              />
            </Form.Item>
          </>
        )}
        <Form.Item>
          <Button block type="primary" htmlType="submit" loading={isLoading}>
            Submit
          </Button>
        </Form.Item>
      </Form>
    </>
  )
}
